/// <amd-module name="Repos/Discussion/Models/DiscussionThreadModel" />
import {
    DiscussionStatus,
    IDiscussionComment,
    IDiscussionCommentModel,
    IDiscussionThread,
    IDiscussionThreadModel
} from "Repos/Discussion/Common/DiscussionContracts";
import {
    IObservableArray,
    IObservableValue,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableValue
} from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { DiscussionCommentModel } from "./DiscussionCommentModel";

let threadId = 0;
let commentId = 0;

export interface IDiscussionThreadModelProps<TThread extends IDiscussionThread = IDiscussionThread> {
    onPersist?: (threadId: number) => void;
    thread: TThread;
}

export class DiscussionThreadModel<TThread extends IDiscussionThread = IDiscussionThread> implements IDiscussionThreadModel<TThread> {
    private props: IDiscussionThreadModelProps<TThread>;
    private originalThread: TThread;

    private comments: IObservableArray<IDiscussionCommentModel>;
    private dirty: IObservableValue<boolean>;
    private error: IObservableValue<Error | null>;
    private status: IObservableValue<DiscussionStatus>;
    private visible: IObservableValue<boolean>;
    private firstComment: IObservableValue<IDiscussionCommentModel>;
    private unread: IObservableValue<boolean>;
    private expanded: IObservableValue<boolean>;

    constructor(props: IDiscussionThreadModelProps<TThread>) {
        this.props = props;

        const { thread } = this.props;
        this.originalThread = thread;
        this.originalThread.comments = thread.comments || [];

        const discussionModels = this.originalThread.comments.map(
            comment => new DiscussionCommentModel({ comment, onDirtyChange: this.onDirtyChange })
        );
        discussionModels.push(new DiscussionCommentModel({ comment: this.getEmptyComment(), onDirtyChange: this.onDirtyChange }));
        this.comments = new ObservableArray(discussionModels);

        this.dirty = new ObservableValue(false);
        this.error = new ObservableValue(null);
        this.status = new ObservableValue(thread.status);
        this.visible = new ObservableValue(true);
        this.firstComment = new ObservableValue(this.findFirstComment());
        this.unread = new ObservableValue(false);
        this.expanded = new ObservableValue(true);
    }

    public get id(): number {
        return this.originalThread.id;
    }

    public get thread(): TThread {
        return this.originalThread;
    }

    public addComment(comment: IDiscussionComment): void {
        const inputCommentIndex = this.comments.value.findIndex(c => c.getId() < 0);
        const newCommentIndex = inputCommentIndex >= 0 ? inputCommentIndex : this.comments.length;

        // Revert input comment
        this.getInputComment().revertComment();

        // Add new comment to the observer list
        this.comments.splice(newCommentIndex, 0, new DiscussionCommentModel({ comment, onDirtyChange: this.onDirtyChange }));

        // Add new comment to the original list
        this.originalThread.comments!.push(comment);
    }

    public deleteComment(commentId: number): void {
        this.findAndApply(commentId, comment => comment.delete());
    }

    public getComments(): IReadonlyObservableArray<IDiscussionCommentModel> {
        return this.comments;
    }

    public finishSaving(commentId: number): void {
        this.findAndApply(commentId, comment => comment.setSaving(false));
    }

    public getError(): IReadonlyObservableValue<Error | null> {
        return this.error;
    }

    public getFirstComment(): IObservableValue<IDiscussionCommentModel> {
        return this.firstComment;
    }

    public getInputComment(): IDiscussionCommentModel {
        return this.comments.value.filter(c => c.getId() < 0)[0];
    }

    public getParentCommentId(): number {
        const comments = this.comments.value;
        return comments.length > 0 ? comments[0].getId() : 1;
    }

    public getStatus(): IObservableValue<DiscussionStatus> {
        return this.status;
    }

    public getVisible(): IObservableValue<boolean> {
        return this.visible;
    }

    public getExpanded(): IObservableValue<boolean> {
         return this.expanded;
    }

    public hasComments(): boolean {
        return this.comments.value.some(c => c.isSaved() && !c.isDeleted().value);
    }

    public isDirty(): IObservableValue<boolean> {
        return this.dirty;
    }

    public isNew(): boolean {
        return this.id < 0;
    }

    public isUnread(): IReadonlyObservableValue<boolean> {
        return this.unread;
    }

    public likeComment(commentId: number, like: boolean, identity: IdentityRef): void {
        this.findAndApply(commentId, comment => comment.setLike(like, identity));
    }

    public setCommentError(commentId: number, error: Error): void {
        this.findAndApply(commentId, comment => comment.setError(error));
    }

    public setError(error: Error | null): void {
        this.error.value = error;
    }

    public setUnread(author: IdentityRef, lastVisitedDate: Date): void {
        for (const comment of this.comments.value) {
            comment.setUnread(author, lastVisitedDate);
        }

        const unread = this.comments.value.some(comment => comment.isUnread().value);
        if (unread !== this.unread.value) {
            this.unread.value = unread;
        }
    }

    public setVisible(visible: boolean): void {
        this.visible.value = visible;
    }

    public startSaving(commentId: number): void {
        this.findAndApply(commentId, comment => comment.setSaving(true));
    }

    public setExpanded(expanded: boolean): void { 
        this.expanded.value = expanded;
    }

    public update(thread: TThread, forceUpdate?: boolean): void {
        if (this.originalThread.lastUpdatedDate !== thread.lastUpdatedDate || forceUpdate) {
            const isNew = this.isNew();
            this.originalThread = thread;
            this.updateStatus(thread.status);

            // Update comments
            this.originalThread.comments = thread.comments || [];

            // Get the comments currently being edited
            const editedComments: Map<number, IDiscussionCommentModel> = new Map();
            this.comments.value.filter(c => c.isEditing().value).forEach(c => editedComments.set(c.getId(), c));

            // Generate comment models for each comment
            const discussionModels = this.originalThread.comments.map(
                comment => new DiscussionCommentModel({ comment, onDirtyChange: this.onDirtyChange })
            );

            // Add input comment
            const inputComment = this.getInputComment();
            if (inputComment) {
                if (isNew) {
                    inputComment.revertComment();
                }
                discussionModels.push(inputComment as DiscussionCommentModel);
            }

            // Update comments
            this.comments.value = discussionModels;

            // See any comment needs recovery not to loose any changes before update
            this.comments.value.forEach(commentModel => {
                const editedComment = editedComments.get(commentModel.getId());
                if (editedComment) {
                    commentModel.setContent(editedComment.getContent().value);
                    commentModel.startEditing();
                }
            });

            this.firstComment.value = this.findFirstComment();

            if (isNew && this.props.onPersist) {
                this.props.onPersist(thread.id);
            }

            this.dirty.value = editedComments.size > 0;
        }
    }

    public updateStatus(status: DiscussionStatus): void {
        if (this.status.value !== status) {
            this.status.value = status;
            this.originalThread.status = status;
        }
    }

    public updateComment(comment: IDiscussionComment): void {
        const commentToUpdate = this.comments.value.filter(c => c.getId() === comment.id)[0];
        if (commentToUpdate) {
            commentToUpdate.updateComment(comment);
        }
    }

    private findAndApply(commentId: number, operation: (comment: IDiscussionCommentModel) => void): void {
        const comment = this.comments.value.filter(c => c.getId() === commentId)[0];
        comment && operation(comment);
    }

    private findFirstComment(): IDiscussionCommentModel {
        if (this.id < 0) {
            return this.getInputComment();
        }

        return this.comments.value.filter(c => c.getId() > 0).sort((c1, c2) => c1.getId() - c2.getId())[0];
    }

    private getEmptyComment(): IDiscussionComment {
        return {
            content: "",
            id: --commentId,
            isDeleted: false
        } as IDiscussionComment;
    }

    private onDirtyChange = (): void => {
        const dirty = this.getComments().value.some(c => c.isDirty().value);
        if (this.dirty.value !== dirty) {
            this.dirty.value = dirty;
        }
    };
}

export const newThread = (): IDiscussionThread => {
    const now = new Date();
    return {
        id: --threadId,
        comments: [],
        lastUpdatedDate: now,
        publishedDate: now,
        status: DiscussionStatus.Active
    };
};
